import { useRef, useState, useEffect, FC } from 'react'
import {
    Button,
    InlineAlert,
    Card,
    Modal,
    ProgressBar,
    Spinner,
    Grid,
} from '@design-systems/canvas-ui'
import {
    Icon,
    MediaPlayIcon,
    MediaPauseIcon,
    MediaForwardIcon,
    MediaReverseIcon,
} from '@design-systems/react-icons'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import './audio-player.scss'
import { useLogClick } from 'utils/analytics'
import { useLocation } from 'react-router-dom'
import { useAudioData } from 'hooks/insights/useAudioData'
import { useAbandonTime } from './utils'

const rewindForwardInterval = 15 // seconds

export const AudioPlayer: FC = () => {
    const { t } = useEssentials()
    const { insights } = TRANSLATION_KEYS
    const { audio, transcript } = useAudioData()
    const audioRef = useRef<HTMLAudioElement>(null)
    const location = useLocation()
    const { logClick } = useLogClick(location.pathname)
    const { logAbandon } = useAbandonTime(location.pathname)
    const logAbandonRef = useRef(logAbandon)
    logAbandonRef.current = logAbandon

    const [isPlaying, setIsPlaying] = useState(false)
    const [duration, setDuration] = useState<number | null>(null)
    const [hasError, setHasError] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [showTranscriptModal, setShowTranscriptModal] = useState(false)
    const [progress, setProgress] = useState<number | null>(0)

    // Format duration as "2m 15s"
    const formatDuration = (seconds: number): string => {
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        return `${mins}m ${secs}s`
    }

    // Load audio metadata and set up event listeners
    useEffect(() => {
        const currentAudio = audioRef.current
        if (!currentAudio) return

        const handleLoadedMetadata = () => {
            setDuration(currentAudio?.duration ?? null)
            setIsLoading(false)
        }

        const handleError = () => {
            // Only show error if audio.error exists and isn't null
            const currentAudio = audioRef.current
            if (currentAudio?.error) {
                setHasError(true)
                setIsLoading(false)
            }
        }

        const handleCanPlay = () => {
            // Audio loaded successfully - clear any previous errors
            setHasError(false)
            setIsLoading(false)
        }

        const handleEnded = () => {
            setIsPlaying(false)
        }

        const handleUpdateProgress = () => {
            const currentAudio = audioRef.current
            if (
                currentAudio?.currentTime === null ||
                currentAudio?.currentTime === undefined ||
                currentAudio?.duration === null
            ) {
                setProgress(null)
            } else {
                setProgress(
                    currentAudio?.duration
                        ? (currentAudio.currentTime / currentAudio?.duration) *
                              100
                        : 0,
                )
            }
        }

        currentAudio.addEventListener('loadedmetadata', handleLoadedMetadata)
        currentAudio.addEventListener('error', handleError)
        currentAudio.addEventListener('ended', handleEnded)
        currentAudio.addEventListener('canplay', handleCanPlay)
        currentAudio.addEventListener('timeupdate', handleUpdateProgress)

        return () => {
            // If audio is not at the end or the beginning, consider it abandoned and log the current time
            if (
                currentAudio.currentTime !== currentAudio.duration &&
                currentAudio.currentTime !== 0
            ) {
                logAbandonRef.current(currentAudio.currentTime)
            }
            currentAudio.removeEventListener(
                'loadedmetadata',
                handleLoadedMetadata,
            )
            currentAudio.removeEventListener('error', handleError)
            currentAudio.removeEventListener('ended', handleEnded)
            currentAudio.removeEventListener('canplay', handleCanPlay)
            currentAudio.removeEventListener('timeupdate', handleUpdateProgress)
        }
    }, [audio.url])

    // Play/pause handler
    const handlePlayPause = async () => {
        const currentAudio = audioRef.current
        if (!currentAudio) return

        try {
            if (isPlaying) {
                logClick('audio player pause')
                currentAudio.pause()
                setIsPlaying(false)
            } else {
                await currentAudio.play()
                logClick('audio player play')
                setIsPlaying(true)
            }
        } catch {
            // Audio playback error - set error state
            setHasError(true)
        }
    }

    const handleRewind = () => {
        const currentAudio = audioRef.current
        if (!currentAudio) return
        logClick('audio player rewind')

        currentAudio.currentTime = Math.max(
            0,
            currentAudio.currentTime - rewindForwardInterval,
        )
    }

    const handleFastForward = () => {
        const currentAudio = audioRef.current
        if (!currentAudio) return
        logClick('audio player fast forward')

        currentAudio.currentTime = Math.min(
            currentAudio.duration,
            currentAudio.currentTime + rewindForwardInterval,
        )
    }

    const handleGetTranscript = () => {
        logClick('audio player transcript opened')
        setShowTranscriptModal(true)
    }

    // Cleanup on unmount - pause and reset audio
    useEffect(() => {
        const currentAudio = audioRef.current
        return () => {
            if (currentAudio) {
                currentAudio.pause()
                currentAudio.currentTime = 0
            }
        }
    }, [])

    return (
        <div className="hc8-wth-100">
            {hasError && (
                <InlineAlert
                    variant="error"
                    heading={t(insights.audioPlayer.audioLoadError)}
                    className="hc8-mb-3x"
                >
                    {t(insights.audioPlayer.audioUnavailable)}
                </InlineAlert>
            )}
            {/* Hidden audio element for playback */}
            <audio
                ref={audioRef}
                src={audio.url ?? undefined}
                preload="metadata"
                aria-label={t(insights.audioPlayer.listenToBriefing)}
            />

            {/* Visible player card UI */}
            <Card className="hc8-wth-100 hc8-pa-4x">
                <Grid.Container
                    align="center"
                    spacing="normal"
                    className="hc8-pa-none"
                    direction="row"
                    justify="space-between"
                >
                    <Grid.Item size={{ xs: 8 }}>
                        <Grid.Container
                            direction="row"
                            spacing="normal"
                            justify="start"
                        >
                            <Grid.Item>
                                <Button
                                    variant="primary"
                                    onClick={handlePlayPause}
                                    disabled={isLoading || hasError}
                                    aria-label={
                                        isPlaying
                                            ? 'Pause audio briefing'
                                            : 'Play audio briefing'
                                    }
                                    size="lg"
                                    isIconOnly
                                >
                                    <Icon
                                        svg={
                                            isPlaying
                                                ? MediaPauseIcon
                                                : MediaPlayIcon
                                        }
                                        size="lg"
                                    />
                                </Button>
                            </Grid.Item>
                            <Grid.Item>
                                <Grid.Container
                                    direction="column"
                                    spacing="none"
                                >
                                    <Grid.Item>
                                        <strong className="hc8-fs-150">
                                            {t(
                                                insights.audioPlayer
                                                    .listenToBriefing,
                                            )}
                                        </strong>
                                    </Grid.Item>
                                    <Grid.Item>
                                        {duration && formatDuration(duration)}
                                        {duration && ' • '}
                                        {t(insights.audioPlayer.aiAudioRecap)}
                                    </Grid.Item>
                                </Grid.Container>
                            </Grid.Item>
                        </Grid.Container>
                    </Grid.Item>
                    <Grid.Item size={{ xs: 4 }}>
                        <img
                            src="/insights/AudioWaves.svg"
                            alt=""
                            aria-hidden="true"
                            className="audioIcon"
                        />
                    </Grid.Item>
                </Grid.Container>
                <>
                    <ProgressBar
                        className="hc8-mv-4x"
                        percentComplete={progress ?? 0}
                        isIndeterminate={progress === null}
                    />
                    <Grid.Container
                        direction="row"
                        justify="space-between"
                        align="center"
                    >
                        <Grid.Item>
                            <Button
                                onClick={() => {
                                    handleGetTranscript()
                                }}
                                size="sm"
                                variant="ghost"
                            >
                                {t(insights.audioPlayer.viewTranscript)}
                            </Button>
                        </Grid.Item>
                        <Grid.Item>
                            <Grid.Container
                                direction="row"
                                spacing="normal"
                                justify="end"
                            >
                                <Grid.Item>
                                    <Button
                                        variant="ghost"
                                        onClick={handleRewind}
                                        disabled={isLoading || hasError}
                                        aria-label={t(
                                            insights.audioPlayer.rewind,
                                        )}
                                        size="sm"
                                    >
                                        {t(insights.audioPlayer.fifteenSeconds)}
                                        <Icon
                                            svg={MediaReverseIcon}
                                            size="sm"
                                        />
                                    </Button>
                                </Grid.Item>
                                <Grid.Item>
                                    <Button
                                        variant="ghost"
                                        onClick={handleFastForward}
                                        disabled={isLoading || hasError}
                                        aria-label={t(
                                            insights.audioPlayer.fastForward,
                                        )}
                                        size="sm"
                                    >
                                        {t(insights.audioPlayer.fifteenSeconds)}
                                        <Icon
                                            svg={MediaForwardIcon}
                                            size="sm"
                                        />
                                    </Button>
                                </Grid.Item>
                            </Grid.Container>
                        </Grid.Item>
                    </Grid.Container>
                </>
            </Card>

            <Modal
                headingText={t(insights.audioPlayer.transcript)}
                isOpen={showTranscriptModal}
                onClose={() => {
                    logClick('audio player transcript closed')
                    setShowTranscriptModal(false)
                }}
                className="insights-transcript-modal"
                scrollBehavior="contained"
            >
                {transcript.isLoading ? (
                    <Spinner />
                ) : (
                    transcript.data && <p>{transcript.data}</p>
                )}
                {transcript.error && (
                    <InlineAlert
                        variant="error"
                        heading={t(insights.audioPlayer.transcriptUnavailable)}
                    >
                        {transcript.error}
                    </InlineAlert>
                )}
            </Modal>
        </div>
    )
}
